import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IFlightPairingDetails,
  IFullFlight,
  IFullPairingPosition,
  IUser,
  sortCrewByDisplayOrder,
} from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import _ from "lodash";

import { Grid, Button, styled } from "@mui/material";

//Formik
import {
  FormikCheckboxField,
  FormikSelectField,
  FormikTextField,
} from "src/components/FormikComponents/FormikWrappers";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";

//Yup Validation
import * as Yup from "yup";
import { addSelectedFlight } from "src/redux/actions/flights/FlightData.Actions";
import { FormTabId, IFormHandle } from "src/pages/TimelineEventEditors/helpers";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { IRootState } from "src/redux/reducers/CombinedReducers";

export interface IFlightLogProps {
  windowId: string;
  flight: IFullFlight;
  updateTabState?: (dirtyStatus: boolean) => void; // update parent with dirty status.
  crewOnFlight: Record<string, IFlightPairingDetails[]>;
  setLoadedFlight: (flightValues: IFullFlight) => void;
  registerFormHandle?: (tabId: string, handle: IFormHandle | null) => void;
  saveAllDirtyTabs?: (excludeTabId?: string) => Promise<void>;
}

type LogForm = FormikProps<Partial<IFullFlight>>;

const OCFlightLog = styled(Grid)(({ theme }) => ({
  "& .loading_spinner": {
    marginTop: -50,
    marginLeft: -26,
  },
  "& .form_body": {
    "& .input_control": {
      padding: "0px 10px",
      "& .react-select-container": {
        width: 263,
      },
    },
    "& .form_footer": {
      marginTop: 10,
      display: "flex",
      justifyContent: "center",
      "& button": { margin: "5px 5px" },
      "& .btn": {
        flexWrap: "wrap",
        display: "flex",
        height: 27,
        fontSize: 10,
        lineHeight: "12px",
        padding: "2px 8px",
        margin: "0px 4px",
        "&.submit": {
          color: theme.custom.forms.inputBack,
        },
      },
    },
  },
}));

// Log Form Validation with YUP
const LogSchema = Yup.object().shape({
  takeOff: Yup.string().nullable(),
  landing: Yup.string().nullable(),
  sector: Yup.array().nullable(),
  cycles: Yup.number().min(1, "Cycles must be greater than 1").integer("Cycles must be a integer"),
  aircraftAirtime: Yup.number().nullable().typeError("A numeric value is expected"),
  aircraftCycles: Yup.number()
    .nullable()
    .typeError("A numeric value is expected")
    .integer("Aircraft Cycles must be an integer"),
  isVFR: Yup.boolean(),
});

/**
 * A wraper commponet to display the componet to add a comment to a flight  &
 * display all the comments currently on a flight
 * @param props - flight: IFullEvent
 */

export const FlightLog = (props: IFlightLogProps): JSX.Element => {
  const dispatch = useDispatch();

  const { windowId, flight, updateTabState, crewOnFlight, setLoadedFlight, registerFormHandle, saveAllDirtyTabs } =
    props;
  // const [isLoading, setIsLoading] = useState<boolean>(true);

  const formikRef = useRef<FormikProps<Partial<IFullFlight>>>(null);
  const enableVFR = useSelector((state: IRootState) => state.UISettings.enableVFR);

  // Register form handle so parent can trigger this tab's submit for save-all
  useEffect(() => {
    registerFormHandle?.(FormTabId.Log, {
      submitForm: () => formikRef.current?.submitForm() ?? Promise.resolve(),
      isDirty: () => formikRef.current?.dirty ?? false,
    });
    return () => {
      registerFormHandle?.(FormTabId.Log, null);
    };
  }, [registerFormHandle]);

  const crewMembers: IUser[] = useSelector((state: IRootState) => state.structures.appUsers);

  const crewOptions: Partial<IFullPairingPosition>[] = useMemo(() => {
    //You may have the case where a flight is removed from pairing
    //or a crew is is removed from a pairing but a crew has already be added one of the fields
    // still want to show that crew to the user
    // BUT removed crew shoudln't be an option anymore to add to one of the fields
    if (crewOnFlight?.crew && crewMembers) {
      const curCrewId = [flight.landing, flight.takeOff, ...flight.sector];
      const curCrew: Partial<IFullPairingPosition>[] = [];
      curCrewId.forEach((cur: string) => {
        const found = crewMembers.find((crewMember: IUser) => crewMember._id === cur);
        if (found) curCrew.push({ crew: found });
      });
      const crewOnFlt = crewOnFlight?.crew
        .map((c: IFlightPairingDetails) => c.position)
        .filter((crew: IFullPairingPosition) => crew?.position?.isPilot)
        .sort(sortCrewByDisplayOrder);
      const allCrew: Partial<IFullPairingPosition>[] = [...crewOnFlt, ...curCrew];
      const uniqueCrew = _.uniqBy(allCrew, _.iteratee("crew._id"));
      return uniqueCrew;
    }
    return [];
  }, [crewMembers, crewOnFlight?.crew, flight.landing, flight.sector, flight.takeOff]);

  const mosaicPane = useMemo(() => {
    return windowId ? document.getElementById(`${windowId}-panel`) : null;
  }, [windowId]);

  const initialValues = useMemo(() => {
    return {
      takeOff: flight.takeOff ?? undefined,
      landing: flight.landing ?? undefined,
      sector: flight.sector?.length > 0 ? flight.sector : [],
      cycles: flight.cycles ?? 1,
      aircraftAirtime: flight.aircraftAirtime ?? undefined,
      aircraftCycles: flight.aircraftCycles ?? undefined,
      isVFR: flight.isVFR ?? false,
    };
  }, [flight.landing, flight.sector, flight.takeOff, flight.cycles, flight.aircraftAirtime, flight.aircraftCycles, flight.isVFR]);

  const handleSubmit = async (
    values: Partial<IFullFlight>,
    helpers: FormikHelpers<Partial<IFullFlight>>
  ): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;

    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Updating...",
      successMessage: "Flight Successfully Updated",
      errorMessage: "Error: Unable to Update Flight",
    };

    const response = await AmplifyAPIWrapper.Put<any>(`/flights/${flight._id}`, values, notifications);
    if (response?.newEvent?._id) {
      resetForm({ values });
      setStatus({ success: true });
      //update form
      dispatch(addSelectedFlight(response.newEvent?._id));
      // update loaded flight in flight editor
      setLoadedFlight(response?.newEvent);

      // Trigger save on all other dirty tabs
      await saveAllDirtyTabs?.(FormTabId.Log)?.catch((error) => console.error("Failed to save all dirty tabs:", error));
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };
  //Show user that there are unassigned crew but don't allow them to select them
  //removed crew shoudln't be an option to select
  const isCrewOptionDisabled = useCallback(
    (option: IFullPairingPosition): boolean => {
      //is the position assigned, is the crew active and is the crew still assigned on the flight?
      return option.crew &&
        option.crew?.crewInfo?.isActive &&
        crewOnFlight?.crew.find((c: any) => {
          return c.position.crew._id === option.crew._id;
        })
        ? false
        : true;
    },
    [crewOnFlight?.crew]
  );

  return (
    <OCFlightLog container direction={"column"}>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={LogSchema}
        innerRef={formikRef}
        validate={(values) => {
          if (updateTabState) {
            const logIsDirty = !_.isEqual(initialValues, values);
            updateTabState(logIsDirty);
          }
        }}
        onReset={() => {
          if (updateTabState) updateTabState(false);
        }}
        validateOnBlur={false} //added this to stop the required message appearing on initial selection of rider
      >
        {(logForm: LogForm) => {
          const { dirty, isValid, isSubmitting, handleReset } = logForm;
          return (
            <Form>
              <Grid container className="form_body">
                <Grid container direction="row">
                  <FormikSelectField
                    className="field selectfield"
                    name="takeOff"
                    label="Take Off"
                    options={crewOptions}
                    parse={(crew: Partial<IFullPairingPosition>) => {
                      return crew.crew?._id;
                    }}
                    formatOptionLabel={(option: Partial<IFullPairingPosition>) => {
                      const user = option.crew;
                      return (
                        <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                          {option.position && (
                            <PositionBadge
                              position={option.position}
                              filled={!!user}
                              isInactive={!user?.crewInfo?.isActive}
                            />
                          )}
                          <Grid item style={{ marginLeft: 10 }}>
                            {user
                              ? `${user?.firstName} ${user?.lastName} ${
                                  user?.employeeId ? `[${user?.employeeId}]` : ""
                                }`
                              : "Unassigned"}
                          </Grid>
                        </Grid>
                      );
                    }}
                    getOptionValue={(option: Partial<IFullPairingPosition>) => {
                      return option.crew?._id;
                    }}
                    placeholder="Select Crew"
                    format={(crewId: string) => {
                      const crew: Partial<IFullPairingPosition> | undefined = crewOptions?.find(
                        (option: Partial<IFullPairingPosition>) => option?.crew?._id === crewId
                      );
                      return crew;
                    }}
                    isOptionDisabled={isCrewOptionDisabled}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isSearchable={true}
                    isClearable={false}
                    menuPortalTarget={mosaicPane}
                    menuPosition="fixed"
                  />

                  <FormikSelectField
                    className="field selectfield"
                    name="landing"
                    label="Landing"
                    options={crewOptions}
                    parse={(crew: Partial<IFullPairingPosition>) => {
                      return crew.crew?._id;
                    }}
                    formatOptionLabel={(option: Partial<IFullPairingPosition>) => {
                      const user = option.crew;
                      return (
                        <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                          {option.position && (
                            <PositionBadge
                              position={option.position}
                              filled={!!user}
                              isInactive={!user?.crewInfo?.isActive}
                            />
                          )}
                          <Grid item style={{ marginLeft: 10 }}>
                            {user
                              ? `${user?.firstName} ${user?.lastName} ${
                                  user?.employeeId ? `[${user?.employeeId}]` : ""
                                }`
                              : "Unassigned"}
                          </Grid>
                        </Grid>
                      );
                    }}
                    getOptionValue={(option: Partial<IFullPairingPosition>) => {
                      return option.crew?._id;
                    }}
                    placeholder="Select Crew"
                    format={(crewId: string) => {
                      const crew: Partial<IFullPairingPosition> | undefined = crewOptions?.find(
                        (option: Partial<IFullPairingPosition>) => option?.crew?._id === crewId
                      );
                      return crew;
                    }}
                    isOptionDisabled={isCrewOptionDisabled}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isSearchable={true}
                    isClearable={false}
                    menuPortalTarget={mosaicPane}
                    menuPosition="fixed"
                  />

                  <FormikSelectField
                    className="field selectfield"
                    name="sector"
                    label="Sector"
                    options={crewOptions}
                    parse={(option: Partial<IFullPairingPosition>[]) => {
                      return option.map((opt) => opt.crew?._id);
                    }}
                    formatOptionLabel={(option: Partial<IFullPairingPosition>) => {
                      const user = option.crew;
                      return (
                        <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                          {option.position && (
                            <PositionBadge
                              position={option.position}
                              filled={!!user}
                              isInactive={!user?.crewInfo?.isActive}
                            />
                          )}
                          <Grid item style={{ marginLeft: 10 }}>
                            {user
                              ? `${user?.firstName} ${user?.lastName} ${
                                  user?.employeeId ? `[${user?.employeeId}]` : ""
                                }`
                              : "Unassigned"}
                          </Grid>
                        </Grid>
                      );
                    }}
                    getOptionValue={(option: Partial<IFullPairingPosition>) => {
                      return option.crew?._id;
                    }}
                    placeholder="Select Crew"
                    format={(crewIds: string[]) => {
                      const crew: Partial<IFullPairingPosition>[] = crewIds.map((crewId) =>
                        crewOptions?.find((option: Partial<IFullPairingPosition>) => option?.crew?._id === crewId)
                      );
                      return crew;
                    }}
                    isOptionDisabled={isCrewOptionDisabled}
                    isMulti={true}
                    isClearable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isSearchable={true}
                    menuPortalTarget={mosaicPane}
                    menuPosition="fixed"
                  />

                  <FormikTextField
                    className="field"
                    label="Cycles"
                    name="cycles"
                    type="number"
                    defaultValue={1}
                    validate={(value: string) => {
                      return parseInt(value) < 1 ? "Cycles must be greater than 1" : "";
                    }}
                  />

                  <FormikTextField
                    className="field"
                    label="Total Aircraft Air Time"
                    name="aircraftAirtime"
                    type="number"
                    placeholder="Total air time (hrs)"
                  />

                  <FormikTextField
                    className="field"
                    label="Total Aircraft Cycles"
                    name="aircraftCycles"
                    type="number"
                    placeholder="Total cycles"
                  />

                  {enableVFR && (
                    <FormikCheckboxField
                      className="field"
                      name="isVFR"
                      label="Visual Flight Rules"
                      labelPlacement="start"
                    />
                  )}
                </Grid>

                <Grid container className="form_footer">
                  <Button
                    className="btn reset"
                    type="reset"
                    variant="contained"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    className={"btn save"}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !isValid || !dirty}
                    type="submit"
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </OCFlightLog>
  );
};
